import { useState, useRef, useEffect, useCallback } from 'react';

/**
 * Anti rage-click guard para submit de formulário.
 *
 * Telemetria mostrou que os botões `Continuar` em /cpf, /email, /telefone, /nome
 * acumulavam ~11 rage clicks/dia (3+ cliques no mesmo botão em <1s). Causa: o
 * paciente clica, vê o botão estático, e clica de novo enquanto a navegação
 * é processada. Sem feedback imediato, ele acha que travou.
 *
 * Como usar:
 *   const [isSubmitting, guarded] = useFormSubmitGuard(handleConfirm);
 *   ...
 *   <button onClick={guarded} disabled={isSubmitting || !isValid}>
 *     {isSubmitting ? <Spinner/> + ' Continuando…' : 'Continuar'}
 *   </button>
 *
 * O guarded:
 *  - bloqueia chamadas concorrentes enquanto isSubmitting === true
 *  - reseta isSubmitting depois de cooldownMs (default 1500ms)
 *  - se o handler retornar literalmente `false`, libera imediatamente — útil
 *    quando a validação falha e queremos re-habilitar o botão para o paciente
 *    corrigir o input e tentar de novo (sem esperar 1.5s).
 *  - se o handler lançar, libera imediatamente também.
 *  - `holdUntilSettled: true` mantém o botão travado até o handler terminar,
 *    mesmo que passe do cooldown. Sem isso, um POST lento (o
 *    /registration/complete chega a 8s no p90) reabre o botão no meio da
 *    espera e o paciente toca de novo achando que falhou.
 *
 * Cleanup: limpa timer no unmount, evita warning de "setState após unmount".
 */
export default function useFormSubmitGuard(handler, { cooldownMs = 1500, holdUntilSettled = false } = {}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const timerRef = useRef(null);
  const submittingRef = useRef(false);
  const pendingRef = useRef(false);
  const cooldownDoneRef = useRef(false);
  const mountedRef = useRef(true);

  useEffect(
    () => {
      mountedRef.current = true;
      return () => {
        mountedRef.current = false;
        if (timerRef.current) clearTimeout(timerRef.current);
      };
    },
    [],
  );

  const guarded = useCallback(
    async (...args) => {
      if (submittingRef.current) return undefined;
      submittingRef.current = true;
      pendingRef.current = true;
      cooldownDoneRef.current = false;
      setIsSubmitting(true);
      const release = () => {
        if (timerRef.current) clearTimeout(timerRef.current);
        submittingRef.current = false;
        if (mountedRef.current) setIsSubmitting(false);
      };
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => {
        cooldownDoneRef.current = true;
        if (holdUntilSettled && pendingRef.current) return;
        release();
      }, cooldownMs);
      try {
        const result = await handler(...args);
        pendingRef.current = false;
        if (result === false || (holdUntilSettled && cooldownDoneRef.current)) release();
        return result;
      } catch (err) {
        pendingRef.current = false;
        release();
        throw err;
      }
    },
    [handler, cooldownMs, holdUntilSettled],
  );

  return [isSubmitting, guarded];
}
